import { Injectable } from '@angular/core';
import { AccessoryCharge, Charge, PaymentCart, PaymentCartType, PaymentExternalData } from '@cargos/sprintpay-models';
import dayjs from 'dayjs';
import { ShipmentFormType } from 'src/app/modules/polar/pages/prepay/shipment-form/models/shipment-form';
import { CurrentFacilityService } from '.';

export interface ComplementaryData {
    companyId?: number | null;
    customerReference?: string;
    nameFiles?: string;
    awb?: string;
    origin?: string;
    destination?: string;
    arrivalDate?: string;
    source?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dynamicValues?: any;
    externalData?: PaymentExternalData | undefined;
}

@Injectable({
    providedIn: 'root',
})
export class ParseToPaymentCartService {
    constructor(private currentFacilityService: CurrentFacilityService) {}

    private parseChargeToPaymentCart(charge: Charge, complementaryChargeData?: ComplementaryData): PaymentCart {
        const companyId = complementaryChargeData?.companyId?.toString();
        const customerReference = complementaryChargeData?.customerReference || undefined;

        const paymentCart: PaymentCartType = {
            amount: charge.amount,
            awb: complementaryChargeData?.awb || charge.awbId,
            hawb: charge.hawbId,
            origin: complementaryChargeData?.origin,
            destination: complementaryChargeData?.destination || charge.destination,
            arrivalDate: complementaryChargeData?.arrivalDate,
            dynamicValues: complementaryChargeData?.dynamicValues,
            attachments: complementaryChargeData?.nameFiles || undefined,
            externalData: {
                ...complementaryChargeData?.externalData,
                awbId: charge.awbId,
                chargeId: charge.chargeId,
                paymentType: charge.paymentType,
                flightNumber: charge?.flightNumber,
                flightDate: charge?.flightDate,
            },
            source: complementaryChargeData?.source,
            paymentType: charge.paymentType,
        };
        if (customerReference) {
            paymentCart['customerReference'] = customerReference;
        }
        if (companyId) {
            paymentCart['company'] = { id: companyId };
        }
        Object.keys(paymentCart).forEach(
            (key) => (paymentCart[key] === undefined || paymentCart[key] === '') && delete paymentCart[key]
        );

        return PaymentCart.fromJson(paymentCart);
    }

    private parseAccessoryToPaymentCart(
        accessory: AccessoryCharge,
        complementaryAccessoryData?: ComplementaryData
    ): PaymentCart {
        const companyId = complementaryAccessoryData?.companyId?.toString();

        const paymentCart: PaymentCartType = {
            amount: accessory.amount,
            awb: complementaryAccessoryData?.awb,
            hawb: accessory.hawbId,
            origin: complementaryAccessoryData?.origin,
            destination: complementaryAccessoryData?.destination || accessory.destinations,
            arrivalDate: complementaryAccessoryData?.arrivalDate,
            dynamicValues: complementaryAccessoryData?.dynamicValues,
            attachments: complementaryAccessoryData?.nameFiles || undefined,
            externalData: {
                ...complementaryAccessoryData?.externalData,
                paymentType: accessory?.paymentType,
                flightNumber: accessory?.flightNumber || '',
                flightDate: accessory?.flightDate || '',
            },
            source: complementaryAccessoryData?.source,
            customerReference: complementaryAccessoryData?.customerReference || undefined,
            isAccessorial: true,
        };

        if (companyId) {
            paymentCart['company'] = { id: companyId };
        }

        Object.keys(paymentCart).forEach(
            (key) => (paymentCart[key] === undefined || paymentCart[key] === '') && delete paymentCart[key]
        );

        return PaymentCart.fromJson(paymentCart);
    }

    parseShipmentToPaymentCart(shipment: ShipmentFormType): PaymentCart {
        const source = this.currentFacilityService.getFacilitySource();
        const date = dayjs().format('YYYY-MM-DD');

        const paymentCart: PaymentCartType = {
            amount: parseFloat(shipment.amount.replace(/[$,]/g, '')),
            awb: shipment.awb,
            origin: shipment.origin,
            destination: shipment.destination,
            externalData: {
                pickupDate: date,
                source,
                accountNumber: shipment.accountNumber,
                agentName: shipment.agentName,
                iataNumber: shipment.iataNumber,
                paymentType: 'Invoice-Charges',
                typeWeight: shipment.typeWeight,
                weight: shipment.weight,
            },
            source,
            attachments: shipment.attachments,
        };

        return PaymentCart.fromJson(paymentCart);
    }
}
